(function () {
  'use strict';

  /** @type {Window & Record<string, any>} */
  const runtimeWindow = window;
  const INIT_FLAG = '__aoneGoldPriceScriptInitialized';
  const CONTROLLER_KEY = '__aoneGoldPriceController';
  const CONTAINER_SELECTOR = '[data-gold-price-container]';
  const VALUE_SELECTOR = '[data-gold-price-value]';

  if (runtimeWindow[INIT_FLAG]) return;
  runtimeWindow[INIT_FLAG] = true;

  class GoldPriceController {
    constructor() {
      /** @type {number | null} */
      this.intervalId = null;
      /** @type {HTMLElement | null} */
      this.valueElement = null;
      /** @type {string} */
      this.configSignature = '';
      /** @type {{ enabled: boolean; baseUrl: string; autoRefresh: boolean; intervalMs: number } | null} */
      this.config = null;
      /** @type {number | null} */
      this.lastSuccessfulPrice = null;
      /** @type {string} */
      this.lastRenderedText = '';
      /** @type {boolean} */
      this.fetchInFlight = false;
      /** @type {AbortController | null} */
      this.abortController = null;
    }

    /** @param {HTMLElement} container */
    mount(container) {
      if (!(container instanceof HTMLElement)) return;

      const valueElement = container.querySelector(VALUE_SELECTOR);
      if (!(valueElement instanceof HTMLElement)) return;

      const config = this.readConfig(container, valueElement);
      const configSignature = this.createConfigSignature(config);

      if (this.valueElement === valueElement && this.configSignature === configSignature && this.intervalId !== null) {
        return;
      }

      this.clearInterval();
      this.valueElement = valueElement;
      this.config = config;
      this.configSignature = configSignature;

      if (!config.enabled) return;

      if (this.lastSuccessfulPrice === null) {
        this.renderText('Loading...');
      } else {
        this.renderPrice(this.lastSuccessfulPrice);
      }

      this.refresh();

      if (config.autoRefresh) {
        this.intervalId = window.setInterval(() => {
          this.refresh();
        }, config.intervalMs);
      }
    }

    unmount() {
      this.clearInterval();
      this.abortController?.abort();
      this.abortController = null;
      this.fetchInFlight = false;
      this.valueElement = null;
      this.config = null;
      this.configSignature = '';
    }

    clearInterval() {
      if (this.intervalId === null) return;
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }

    /**
     * @param {HTMLElement} container
     * @param {HTMLElement} valueElement
     */
    readConfig(container, valueElement) {
      const enabled = container.dataset.goldPriceEnabled === 'true';
      const autoRefresh = container.dataset.goldPriceAutoRefresh === 'true';
      const baseUrl = (container.dataset.goldPriceApiBaseUrl || '').trim();
      const rawInterval = Number.parseInt(container.dataset.goldPriceRefreshInterval || '', 10);
      const intervalSeconds = Number.isFinite(rawInterval) && rawInterval > 0 ? rawInterval : 60;
      const intervalMs = intervalSeconds * 1000;
      return {
        enabled: enabled && baseUrl.length > 0,
        baseUrl,
        autoRefresh,
        intervalMs,
      };
    }

    /**
     * @param {{ enabled: boolean; baseUrl: string; autoRefresh: boolean; intervalMs: number }} config
     */
    createConfigSignature(config) {
      return [config.enabled, config.autoRefresh, config.baseUrl, config.intervalMs].join('|');
    }

    /** @param {string} baseUrl */
    buildEndpoint(baseUrl) {
      const normalizedBaseUrl = baseUrl.replace(/\/+$/, '');
      return `${normalizedBaseUrl}/api/gold-price/latest`;
    }

    async refresh() {
      if (!this.config || !this.valueElement || this.fetchInFlight) return;

      this.fetchInFlight = true;
      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        const response = await fetch(this.buildEndpoint(this.config.baseUrl), {
          method: 'GET',
          headers: { accept: 'application/json' },
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Gold price request failed with status ${response.status}`);
        }

        const payload = await response.json();
        const nextPrice = this.extractPrice(payload);

        if (nextPrice === null) {
          throw new Error('Gold price response did not include a numeric value');
        }

        if (this.lastSuccessfulPrice !== nextPrice) {
          this.lastSuccessfulPrice = nextPrice;
          this.renderPrice(nextPrice);
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;

        if (this.lastSuccessfulPrice === null) {
          this.renderText('Unavailable');
        }

        console.error('[gold-price] Failed to refresh live gold price', error);
      } finally {
        this.fetchInFlight = false;
      }
    }

    /** @param {any} payload */
    extractPrice(payload) {
      const candidates = [
        payload,
        payload?.data?.latest?.liveGoldPrice,
        payload?.data?.latest?.goldPM,
        payload?.data?.latest?.goldAM,
        payload?.data?.last20SuccessfulUpdates?.[0]?.liveGoldPrice,
        payload?.data?.last20SuccessfulUpdates?.[0]?.goldPM,
        payload?.data?.last20SuccessfulUpdates?.[0]?.goldAM,
        payload?.price,
        payload?.gold_price,
        payload?.goldPrice,
        payload?.latest_price,
        payload?.latestPrice,
        payload?.latest?.price,
        payload?.latest?.gold_price,
        payload?.latest?.goldPrice,
        payload?.data?.price,
        payload?.data?.gold_price,
        payload?.data?.goldPrice,
        payload?.data?.latest?.price,
        payload?.result?.price,
        payload?.result?.gold_price,
        payload?.result?.goldPrice,
      ];

      for (const candidate of candidates) {
        const value = this.toNumber(candidate);
        if (value !== null) return value;
      }

      return null;
    }

    /** @param {unknown} value */
    toNumber(value) {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        const normalized = value.replace(/,/g, '').replace(/[^0-9.-]/g, '');
        if (!normalized) return null;
        const parsed = Number.parseFloat(normalized);
        if (Number.isFinite(parsed)) return parsed;
      }

      return null;
    }

    /** @param {number} value */
    formatPrice(value) {
      return `$${value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    /** @param {number} value */
    renderPrice(value) {
      this.renderText(this.formatPrice(value));
    }

    /** @param {string} nextText */
    renderText(nextText) {
      if (!this.valueElement || this.lastRenderedText === nextText) return;
      this.valueElement.textContent = nextText;
      this.lastRenderedText = nextText;
    }
  }

  /** @type {GoldPriceController} */
  const controller = runtimeWindow[CONTROLLER_KEY] || new GoldPriceController();
  runtimeWindow[CONTROLLER_KEY] = controller;

  /** @param {HTMLElement | Document | unknown} scope */
  function init(scope) {
    const root = scope instanceof HTMLElement || scope instanceof Document ? scope : document;
    const container = root.querySelector(CONTAINER_SELECTOR);

    if (!(container instanceof HTMLElement)) return;

    controller.mount(container);
  }

  /** @param {Event} event */
  function handleSectionUnload(event) {
    const target = /** @type {EventTarget | null} */ (event.target);
    if (!(target instanceof HTMLElement)) return;
    if (!target.querySelector(CONTAINER_SELECTOR)) return;
    controller.unmount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', handleSectionUnload);
})();
